<template>
    <div v-if="visible" class="style-update-overlay" @click.self="closeDialog">
        <section class="style-update-dialog" role="dialog" aria-modal="true" aria-labelledby="styleUpdateTitle">
            <header class="style-update-header">
                <div>
                    <p>STYLE REVIEW APPLICATION</p>
                    <h3 id="styleUpdateTitle">新增摄影风格</h3>
                    <span>每个风格需提交恰好 5 张原创 JPG / PNG 样片，提交后进入独立审核。</span>
                </div>
                <button type="button" aria-label="关闭新增摄影风格" @click="closeDialog">×</button>
            </header>

            <form class="style-update-form" novalidate @submit.prevent="submitUpdate">
                <main>
                    <p v-if="loadingOptions" class="style-update-copy">正在加载可选摄影主题和风格…</p>
                    <p v-else-if="loadError" class="style-update-error" role="alert">{{ loadError }}</p>
                    <template v-else>
                        <label>
                            <span>摄影主题 <b>*</b></span>
                            <div class="style-update-selector">
                                <button type="button" class="style-update-select-trigger" :class="{ 'is-placeholder': !form.themeId }" :disabled="!themeOptions.length" :aria-expanded="openSelect === 'theme'" aria-haspopup="listbox" @click="toggleSelect('theme')">{{ selectedThemeName }}<i aria-hidden="true">⌄</i></button>
                                <div v-if="openSelect === 'theme'" class="style-update-select-menu" role="listbox" aria-label="摄影主题">
                                    <button v-for="theme in themeOptions" :key="theme.themeId" type="button" role="option" :aria-selected="String(form.themeId) === String(theme.themeId)" :class="{ active: String(form.themeId) === String(theme.themeId) }" @click="selectTheme(theme)">{{ theme.themeName }}<i v-if="String(form.themeId) === String(theme.themeId)" aria-hidden="true">✓</i></button>
                                </div>
                            </div>
                        </label>
                        <label>
                            <span>具体风格 <b>*</b></span>
                            <div class="style-update-selector">
                                <button type="button" class="style-update-select-trigger" :class="{ 'is-placeholder': !form.styleId }" :disabled="!form.themeId" :aria-expanded="openSelect === 'style'" aria-haspopup="listbox" @click="toggleSelect('style')">{{ selectedStyleName }}<i aria-hidden="true">⌄</i></button>
                                <div v-if="openSelect === 'style'" class="style-update-select-menu" role="listbox" aria-label="具体风格">
                                    <button v-for="style in styleOptions" :key="style.styleId" type="button" role="option" :disabled="isStyleUnavailable(style)" :aria-selected="String(form.styleId) === String(style.styleId)" :class="{ active: String(form.styleId) === String(style.styleId) }" @click="selectStyle(style)">{{ style.styleName }}<small v-if="isStyleUnavailable(style)">已生效或审核中</small><i v-else-if="String(form.styleId) === String(style.styleId)" aria-hidden="true">✓</i></button>
                                </div>
                            </div>
                        </label>
                        <professional-application-image-upload
                            label="原创样片"
                            accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                            required
                            :max-count="5"
                            :files="form.samples"
                            :preview-urls="form.samplePreviewUrls"
                            hint="请逐张上传 JPG / PNG 图片；每个风格必须上传 5 张。"
                            @select="selectSamples"
                            @remove="removeSample($event)" />
                    </template>
                    <p v-if="formError" class="style-update-error" role="alert">{{ formError }}</p>
                </main>
                <footer>
                    <button type="button" class="style-update-secondary" :disabled="submitting" @click="closeDialog">取消</button>
                    <button type="submit" class="style-update-primary" :disabled="submitting || loadingOptions || !!loadError">{{ submitting ? '正在提交…' : '提交审核' }}</button>
                </footer>
            </form>
        </section>
    </div>
</template>

<script>
import request from "@/tool/request";
import API from "@/tool/api";
import {
    getResultData,
    getResultMessage,
    isResultSuccess,
} from "@/tool/result";
import ProfessionalApplicationImageUpload from "./ProfessionalApplicationImageUpload";
import {
    appendUploadFile,
    removeUploadFile,
} from "@/tool/professionalApplicationUpload.cjs";

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const IMAGE_TYPES = ["image/jpeg", "image/png"];

const emptyForm = () => ({
    themeId: "",
    styleId: "",
    themeStyleId: "",
    samples: [],
    samplePreviewUrls: [],
});

export default {
    name: "PhotographerStyleUpdateDialog",
    components: { ProfessionalApplicationImageUpload },
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        currentStyles: {
            type: Array,
            default: () => [],
        },
        pendingStyles: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            themeOptions: [],
            form: emptyForm(),
            loadingOptions: false,
            loadError: "",
            formError: "",
            submitting: false,
            openSelect: "",
        };
    },
    computed: {
        styleOptions() {
            const theme = this.themeOptions.find((item) => String(item.themeId) === String(this.form.themeId));
            return theme && Array.isArray(theme.styles) ? theme.styles : [];
        },
        selectedThemeName() {
            const theme = this.themeOptions.find((item) => String(item.themeId) === String(this.form.themeId));
            return theme ? theme.themeName : "请选择摄影主题";
        },
        selectedStyleName() {
            const style = this.styleOptions.find((item) => String(item.styleId) === String(this.form.styleId));
            return style ? style.styleName : "请选择具体风格";
        },
    },
    watch: {
        visible: {
            immediate: true,
            handler(nextVisible) {
                if (!nextVisible) return;
                this.resetForm();
                this.loadStyleOptions();
            },
        },
    },
    mounted() {
        document.addEventListener("click", this.handleDocumentClick);
    },
    beforeDestroy() {
        this.revokeSamplePreviews();
        document.removeEventListener("click", this.handleDocumentClick);
    },
    methods: {
        isStyleUnavailable(style) {
            const key = String(style && style.themeStyleId || "");
            return this.currentStyles.concat(this.pendingStyles)
                .some((item) => key && String(item.themeStyleId) === key);
        },
        resetForm() {
            this.revokeSamplePreviews();
            this.form = emptyForm();
            this.formError = "";
            this.openSelect = "";
        },
        toggleSelect(selectName) {
            this.openSelect = this.openSelect === selectName ? "" : selectName;
        },
        selectTheme(theme) {
            this.form.themeId = theme.themeId;
            this.form.styleId = "";
            this.form.themeStyleId = "";
            this.openSelect = "";
        },
        selectStyle(style) {
            if (this.isStyleUnavailable(style)) return;
            this.form.styleId = style.styleId;
            this.form.themeStyleId = style.themeStyleId;
            this.openSelect = "";
        },
        handleDocumentClick(event) {
            const target = event && event.target;
            if (!target || !target.closest || !target.closest(".style-update-selector")) this.openSelect = "";
        },
        closeDialog() {
            if (this.submitting) return;
            this.openSelect = "";
            this.$emit("close");
        },
        loadStyleOptions() {
            this.loadingOptions = true;
            this.loadError = "";
            return request.get(API.creatorCenter.photographer.styleOptions)
                .then((response) => {
                    if (!isResultSuccess(response)) throw response;
                    const data = getResultData(response) || {};
                    this.themeOptions = Array.isArray(data.themes) ? data.themes : [];
                    if (!this.themeOptions.length) {
                        this.loadError = "当前没有可申请的摄影主题／风格，请稍后重试。";
                    }
                })
                .catch((error) => {
                    this.themeOptions = [];
                    this.loadError = getResultMessage(error, "可选摄影风格暂时无法加载，请稍后重试。");
                })
                .finally(() => {
                    this.loadingOptions = false;
                });
        },
        selectSamples(event) {
            const file = event && event.target && event.target.files && event.target.files[0];
            if (!file) return;
            if (this.form.samples.length >= 5) {
                this.formError = "每个新增风格最多只能上传 5 张原创样片。";
                event.target.value = "";
                return;
            }
            if (IMAGE_TYPES.indexOf(file.type) < 0 || file.size > MAX_IMAGE_SIZE) {
                this.formError = "样片仅支持不超过 5 MiB 的 JPG 或 PNG 图片。";
                event.target.value = "";
                return;
            }
            this.form.samples = appendUploadFile(this.form.samples, file, 5);
            this.form.samplePreviewUrls.push(URL.createObjectURL(file));
            this.formError = "";
            event.target.value = "";
        },
        removeSample(index) {
            URL.revokeObjectURL(this.form.samplePreviewUrls[index]);
            this.form.samplePreviewUrls.splice(index, 1);
            this.form.samples = removeUploadFile(this.form.samples, index);
            this.formError = "";
        },
        revokeSamplePreviews() {
            (this.form.samplePreviewUrls || []).forEach((url) => URL.revokeObjectURL(url));
        },
        validateForm() {
            if (!this.form.themeId) return "请选择摄影主题。";
            if (!this.styleOptions.some((item) => String(item.themeStyleId) === String(this.form.themeStyleId))) {
                return "请选择当前主题下的具体风格。";
            }
            if (this.isStyleUnavailable({ themeStyleId: this.form.themeStyleId })) {
                return "该主题和风格组合已生效或正在审核，请选择其他组合。";
            }
            if (this.form.samples.length !== 5) return "每个新增风格必须上传 5 张原创样片。";
            return "";
        },
        buildFormData() {
            const formData = new FormData();
            formData.append("addedStyles[0].themeStyleId", this.form.themeStyleId);
            this.form.samples.forEach((file) => formData.append("addedStyles[0].samples", file));
            return formData;
        },
        submitUpdate() {
            this.formError = this.validateForm();
            if (this.formError || this.submitting) return;

            this.submitting = true;
            request.post(API.creatorCenter.photographer.styleMaterialUpdates, this.buildFormData())
                .then((response) => {
                    if (!isResultSuccess(response)) throw response;
                    this.$message.success(getResultMessage(response, "新增摄影风格已提交审核。"));
                    this.$emit("submitted");
                    this.$emit("close");
                })
                .catch((error) => {
                    this.formError = getResultMessage(error, "风格更新提交失败，请稍后重试。");
                })
                .finally(() => {
                    this.submitting = false;
                });
        },
    },
};
</script>

<style scoped>
.style-update-overlay { position: fixed; z-index: 75; inset: 0; display: grid; place-items: center; padding: 24px; background: rgba(32, 27, 41, .56); }.style-update-dialog { display: flex; width: min(560px, calc(100vw - 48px)); max-height: min(680px, calc(100vh - 48px)); flex-direction: column; overflow: hidden; color: #332d49; background: #fff; border: 1px solid #d9e9f1; border-radius: 20px; box-shadow: 0 24px 70px rgba(30, 42, 58, .28); }.style-update-header { display: flex; gap: 18px; align-items: flex-start; justify-content: space-between; padding: 19px 22px 16px; border-bottom: 1px solid #e7eff3; }.style-update-header p { margin: 0 0 4px; color: #5591ae; font-size: 10px; font-weight: 800; letter-spacing: .12em; }.style-update-header h3 { margin: 0 0 5px; color: #3f3158; font-size: 20px; }.style-update-header span { color: #746c7e; font-size: 12px; line-height: 1.6; }.style-update-header button { display: grid; width: 30px; height: 30px; place-items: center; padding: 0; color: #5d6070; background: #f1f6f8; border: 0; border-radius: 50%; cursor: pointer; font: inherit; font-size: 22px; line-height: 1; }.style-update-form { display: flex; min-height: 0; flex: 1; flex-direction: column; }.style-update-form main { display: grid; gap: 17px; min-height: 0; padding: 22px; overflow: auto; }.style-update-form label { display: grid; gap: 8px; color: #5b5362; font-size: 12px; font-weight: 700; }.style-update-form label > span b { color: #dc657f; }.style-update-form select { width: 100%; min-height: 40px; padding: 8px 10px; color: #514a5a; background: #fff; border: 1px solid #dbe3e8; border-radius: 9px; font: inherit; }.style-update-form select:disabled { color: #a39ba7; background: #f5f4f6; }.style-update-upload { padding: 14px; background: #f8fafb; border: 1px dashed #bad8e6; border-radius: 11px; cursor: pointer; }.style-update-upload strong { color: #3d7f9d; font-size: 13px; }.style-update-upload small, .style-update-upload em { color: #857f76; font-size: 11px; font-style: normal; font-weight: 400; line-height: 1.6; word-break: break-word; }.style-update-upload input { max-width: 100%; color: #5f8092; font-size: 11px; }.style-update-copy { margin: 0; color: #857f76; font-size: 12px; }.style-update-error { margin: 0; color: #a1495c; font-size: 12px; line-height: 1.6; }.style-update-form footer { display: flex; flex: 0 0 auto; justify-content: flex-end; gap: 9px; padding: 14px 22px; border-top: 1px solid #e7eff3; }.style-update-form footer button { min-height: 38px; padding: 8px 15px; border-radius: 9px; cursor: pointer; font: inherit; font-size: 12px; font-weight: 800; }.style-update-secondary { color: #5b7382; background: #fff; border: 1px solid #c9dce5; }.style-update-primary { color: #fff; background: #4f90ae; border: 1px solid #4f90ae; }.style-update-form footer button:disabled { cursor: not-allowed; opacity: .65; }
.style-update-dialog input,
.style-update-dialog select,
.style-update-dialog textarea,
.style-update-dialog option {
    color: #514a5a;
    font-weight: 400;
}

.style-update-dialog input::placeholder,
.style-update-dialog textarea::placeholder {
    color: #a8a3ad;
    font-weight: 400;
    opacity: 1;
}

.style-update-dialog input:focus,
.style-update-dialog select:focus,
.style-update-dialog textarea:focus {
    border-color: #dbe3e8;
    outline: 0;
    box-shadow: none;
}

.style-update-dialog input,
.style-update-dialog select,
.style-update-dialog textarea,
.style-update-dialog option {
    font-weight: 400 !important;
}

.style-update-file-input {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    white-space: nowrap;
}

.style-update-upload-box {
    display: flex;
    min-height: 112px;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 14px;
    background: #fbfcfd;
    border: 1px dashed #bad8e6;
    border-radius: 10px;
    text-align: center;
}

.style-update-upload-box > i {
    display: grid;
    width: 30px;
    height: 30px;
    flex: 0 0 auto;
    place-items: center;
    color: #4f90ae;
    background: #fff;
    border: 1px solid #bad8e6;
    border-radius: 8px;
    font-size: 22px;
    font-style: normal;
    font-weight: 400;
    line-height: 1;
}

.style-update-upload-box > span { display: grid; gap: 2px; }
.style-update-upload-box strong { color: #3d7f9d; font-size: 13px; font-weight: 700; }
.style-update-upload-box small, .style-update-upload-box em { color: #857f76; font-size: 11px; font-style: normal; font-weight: 400; line-height: 1.6; word-break: break-word; }
.style-update-preview-list { display: flex; flex-wrap: wrap; gap: 8px; }.style-update-preview { position: relative; display: grid; width: 96px; gap: 4px; overflow: hidden; }.style-update-preview img { display: block; width: 96px; height: 76px; object-fit: cover; border-radius: 8px; }.style-update-preview span { overflow: hidden; color: #5f8092; font-size: 10px; text-overflow: ellipsis; white-space: nowrap; }.style-update-preview button { position: absolute; top: 3px; right: 3px; display: grid; width: 20px; height: 20px; place-items: center; padding: 0; color: #fff; background: rgba(43, 50, 57, .72); border: 0; border-radius: 50%; cursor: pointer; font: inherit; font-size: 16px; line-height: 1; }

.style-update-header > button {
    flex: 0 0 30px;
    line-height: 1 !important;
    text-align: center;
}

.style-update-selector { position: relative; }
.style-update-select-trigger { display: flex; width: 100%; min-height: 40px; align-items: center; justify-content: space-between; gap: 10px; padding: 8px 11px; color: #514a5a; background: #fff; border: 1px solid #c5dce7; border-radius: 11px; cursor: pointer; font: inherit; font-size: 12px; font-weight: 400; text-align: left; transition: border-color 160ms ease; }
.style-update-select-trigger:hover, .style-update-select-trigger[aria-expanded="true"] { border-color: #6fa9c3; }
.style-update-select-trigger.is-placeholder { color: #857f76; }
.style-update-select-trigger:disabled { cursor: not-allowed; color: #aaa3ae; background: #f5f5f7; border-color: #d9d5dc; }
.style-update-select-trigger i { color: #6692a8; font-size: 16px; font-style: normal; line-height: 1; }
.style-update-select-menu { position: absolute; z-index: 8; top: calc(100% + 7px); right: 0; left: 0; max-height: 216px; padding: 6px; overflow-y: auto; overscroll-behavior: contain; background: #fff; border: 1px solid #bfdae6; border-radius: 13px; box-shadow: 0 12px 24px rgba(65, 101, 120, .16); }
.style-update-select-menu button { display: flex; width: 100%; min-height: 34px; align-items: center; justify-content: space-between; gap: 8px; padding: 7px 9px; color: #4e5962; background: transparent; border: 0; border-radius: 8px; cursor: pointer; font: inherit; font-size: 12px; font-weight: 400; text-align: left; }
.style-update-select-menu button:hover { color: #3e7896; background: #eff8fc; }
.style-update-select-menu button.active { color: #326d8a; background: #e4f4fb; }
.style-update-select-menu button:disabled { cursor: not-allowed; color: #aaa3ae; background: transparent; opacity: 1; }
.style-update-select-menu button small { margin-left: auto; color: #a5804a; font-size: 10px; font-weight: 400; }
.style-update-select-menu button i { color: #4c93b4; font-style: normal; }
</style>
